import React, { useContext,useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AxiosError } from 'axios'

import { CompanyData,EndUser } from '@driftt/api-types'

import * as api from 'api'
import { driftErrorStateBuilder,driftLoadingStateBuilder } from 'components/States'
import { SalesforceContext } from 'context/salesforceContext'
import { AccountDetailsPageParams } from 'hooks/useAccountCompanyTitle'
import { SessionMetrics } from 'hooks/useSessionMetrics'
import { Timeline } from 'typings/timeline'
import { segmentTrack, TrackType } from 'utils/tracking'
import { getURLSearchParam } from 'utils/urlUtils'

import AttributesTab from './AttributesTab'
import ContactDetailsHeader from './ContactDetailsHeader'
import FeedTab from './FeedTab'

import styles from './styles.module.css'

const TABS = ['Feed', 'Attributes']
const TAB_SEARCH_PARAM_KEY = 'tab'
const DEFAULT_TAB = 'Feed'

interface PageProps {
  sessionMetrics: SessionMetrics
}

const ContactDetailsPage: React.FC<PageProps> = props => {
  const { sessionMetrics } = props
  const { id: endUserId } = useParams<AccountDetailsPageParams>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<AxiosError | undefined>()
  const tab = getURLSearchParam(TAB_SEARCH_PARAM_KEY) || DEFAULT_TAB
  const attributeDefs = useContext(SalesforceContext)

  const [activeTab, setActiveTab] = useState<string>(tab)
  const [endUserData, setEndUserData] = useState<EndUser>()
  const [accountData, setAccountData] = useState<any>(null)
  const [unifiedAttributes, setUnifiedAttributes] = useState<any>(null)
  const [companyIntel, setCompanyIntel] = useState<CompanyData | null>(null)
  const [personalIntel, setPersonalIntel] = useState<CompanyData | null>(null)
  const [mostRecentChat, setMostRecentChat] = useState<Timeline | null>(null)
  const [salesforceData, setSalesforceData] = useState<any>(undefined)

  const LoadingComponent = driftLoadingStateBuilder('Loading contact profile...')
  const ErrorComponent = driftErrorStateBuilder(
    'this contact profile',
    'Try going back to a previous page'
  )

  const handleError = (err: any): void => {
    const error: AxiosError = err
    setError(error)
  }

  const fetchAccount = async (orgId: number, domain: string) => {
    try {
      const { data } = await api.findAccount(orgId, domain)
      setAccountData(data)
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
    }
  }

  const fetchUserInfo = async (endUserId: string) => {
    try {
      const { data } = await api.fetchEndUserData(endUserId)
      setEndUserData(data)
    } catch (error) {
      handleError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchUnifiedContact = async (endUserId: string) => {
    try {
      const { data } = await api.getUnifiedContact(endUserId)
      setUnifiedAttributes(data?.mergedAttributes)
      setCompanyIntel(data?.mostRecentDriftIntelData?.companyData)
      setPersonalIntel(data?.mostRecentDriftIntelData?.personalData)
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
    }
  }

  const fetchRecentChat = async (endUserId: string) => {
    try {
      const { data } = await api.fetchEndUserTimeline(endUserId, { size: 1 })
      setMostRecentChat(data.length > 0 ? data[0] : null)
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
    }
  }

  const fetchSalesforceData = async (endUserId: string) => {
    try {
      if (attributeDefs) {
        const { data } = await api.fetchSalesforceRefreshFieldsById(endUserId, attributeDefs)
        setSalesforceData(data)
      }
    } catch (error) {
      setSalesforceData(null)
      console.error(error) // eslint-disable-line no-console
    }
  }

  useEffect(() => {
    fetchSalesforceData(endUserId)
  }, [endUserId, attributeDefs]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUnifiedContact(endUserId)
    fetchUserInfo(endUserId)
    fetchRecentChat(endUserId)
  }, [endUserId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (endUserData?.orgId && companyIntel?.domain) {
      fetchAccount(endUserData?.orgId, companyIntel?.domain)
    }
  }, [endUserData, companyIntel])

  useEffect(() => {
    document.title = 'Drift | Contact Details'
  }, [])

  useEffect(() => {
    if (accountData && companyIntel) {
      segmentTrack(TrackType.CONTACT_DETAILS_PAGE_ACCESSED, {
        accoundId: accountData.id,
        companyName: companyIntel.name
      })
    }
  }, [accountData, companyIntel])

  if (isLoading) {
    return (
      <div className={styles.emptyPage}>
        <div className={styles.loader}>
          <LoadingComponent />
        </div>
      </div>
    )
  }

  if (error?.response?.status === 403) {
    return (
      <div className={styles.emptyPage}>
        <ErrorComponent />
      </div>
    )
  }

  return (
    <div id="contactDetailsPage" className={styles.contactDetailsPage}>
      <div className={styles.header}>
        {endUserData && (
          <ContactDetailsHeader
            tabNames={TABS}
            activeTab={activeTab}
            contact={endUserData}
            onTabSelected={setActiveTab}
            unifiedAttributes={unifiedAttributes}
            personalIntel={personalIntel}
            companyIntel={companyIntel}
            accountData={accountData}
            salesforceData={salesforceData}
          />
        )}
      </div>
      <div className={styles.body}>
        {activeTab === 'Feed' && (
          <FeedTab
            user={endUserData}
            sessionMetrics={sessionMetrics}
            personalIntel={personalIntel}
            unifiedAttributes={unifiedAttributes}
            mostRecentChat={mostRecentChat}
          />
        )}
        {endUserData && activeTab === 'Attributes' && (
          <AttributesTab
            user={endUserData}
            endUserId={endUserId}
            account={accountData}
            salesforceData={salesforceData}
          />
        )}
      </div>
    </div>
  )
}

export default ContactDetailsPage
